import { RequireAuth, RequirePinChanged } from '@saniplus/ui';
import { createBrowserRouter, type RouteObject } from 'react-router';

import { AppLayout } from '../components/AppLayout';
import { RouteError } from '../components/RouteError';
import { pinChangeRoute, publicAuthRoutes } from '../features/auth/routes';
import { hoursRoutes } from '../features/hours/routes';
import { RequirePhotoPrompted } from '../features/profile/RequirePhotoPrompted';
import { photoPromptRoute, profileRoutes } from '../features/profile/routes';
import { punchRoutes } from '../features/punch/routes';
import { shiftsRoutes } from '../features/shifts/routes';
import { tasksRoutes } from '../features/tasks/routes';

/**
 * Library ("data") mode, code-split per route.
 *
 * The nesting is the authorization story, read top to bottom: a session, then
 * a PIN that is not overdue, then the photo offer, then the shell. `/pin`
 * deliberately sits inside `RequireAuth` but outside `RequirePinChanged` — it
 * is the one authenticated screen a user with `mustChangePin` is allowed to
 * reach, and the API agrees (403 `PIN_CHANGE_REQUIRED` everywhere else).
 *
 * `/photo` sits one layer further in, for the same reason and with none of the
 * force: a worker owes the PIN change before anything, meets the photo offer
 * once per session after it (P-01), and can leave it with one tap.
 *
 * ── INTEGRATOR SLOT ────────────────────────────────────────────────────────
 * Feature routes are wired here and nowhere else. Each feature exports a
 * `RouteObject[]` from its own `routes.tsx`; this file imports it and drops it
 * into `shellRoutes` below. Two features are still owed:
 *
 * (none outstanding — `punchRoutes` landed with W3 + W8.)
 *
 * Add them to the spread and delete the corresponding line from this comment.
 * Nothing else changes: the shell, the guards and the tab bar are already
 * pointing at `/`, `/quarts`, `/heures` and `/profil`.
 */
const shellRoutes: RouteObject[] = [
  ...punchRoutes,
  ...shiftsRoutes,
  ...hoursRoutes,
  ...tasksRoutes,
  ...profileRoutes,
];

export const routes: RouteObject[] = [
  {
    ErrorBoundary: RouteError,
    children: [
      ...publicAuthRoutes,
      {
        Component: RequireAuth,
        children: [
          pinChangeRoute,
          {
            Component: RequirePinChanged,
            children: [
              photoPromptRoute,
              {
                Component: RequirePhotoPrompted,
                children: [{ Component: AppLayout, children: shellRoutes }],
              },
            ],
          },
        ],
      },
      {
        path: '*',
        lazy: async () => ({ Component: (await import('../pages/NotFoundPage')).NotFoundPage }),
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
